import {
  type AndOrCondition,
  type EventFilter,
  type RootCondition,
  type PropertyCondition,
  IntervalUnit,
  type OrCondition,
  type AndCondition,
  RelativeDirection,
  ColumnType,
} from "../../query/visual/types";
import * as yup from "yup";
import { audienceAndOrConditionSchema } from "../../query/visual/schema";
import { isArray } from "lodash";
import { JourneyContextSource } from "./context-source";
import { ChannelType } from "../../cep/types/config-types";
import {
  INAPP_EXPIRATION_UNITS,
  type InAppMessageExpiration,
} from "../../cep/inapp-messages/expiry";
import {
  JourneyNodeType,
  JourneyNodeSyncMode,
  MISSING_WAREHOUSE_DATA_BEHAVIORS,
  type MissingWarehouseDataBehavior,
  type JourneySyncTimeoutAction,
  SYNC_TIMEOUT_ACTIONS,
} from "./journey-node-types";
import type { RealtimeCondition } from "../realtime-conditions";
import {
  FM_COLUMN_TYPES,
  type FMColumnType,
} from "../../customer-data/decision-engine/column-types";

export {
  DEFAULT_MISSING_WAREHOUSE_DATA_BEHAVIOR,
  DEFAULT_SYNC_TIMEOUT_ACTION,
  type MissingWarehouseDataBehavior,
  JourneyNodeSyncMode,
  JourneyNodeType,
  type JourneySyncTimeoutAction,
  SYNC_TIMEOUT_ACTIONS,
} from "./journey-node-types";

//
// This file contains types that appear in the database, for example config
// types that add structure to a JSONB column, or enum types that add
// constraints to a string column representing an enum. All field names should
// match what is in the database exactly so we can use them directly on top of
// database query results.

export type JourneyExecutionMode = "batch" | "realtime";

export const SKIP_EXIT_CRITERIA_EVALUATION_NODE_TYPES = [
  JourneyNodeType.EntryEvent,
  JourneyNodeType.EntryCohort,
  JourneyNodeType.EntryAudienceExperiment,
];

export type NoOpConfig = {
  type: JourneyNodeType.NoOp;
};

export type EntryConfig = {
  max_num_entries: number;
  exclusions?: {
    type: "any_journey" | "specific_journey";
    journey_ids?: string[];
  };
  re_entry_delay?: {
    quantity: number;
    unit: IntervalUnit;
  };
};

export type EntryCohortConfig = EntryConfig & {
  type: JourneyNodeType.EntryCohort;
};

const exclusionsSchema = yup
  .object()
  .shape({
    type: yup.string().oneOf(["any_journey", "specific_journey"]),
    journey_ids: yup
      .array()
      .of(yup.string())
      .when("type", {
        is: "specific_journey",
        then: yup
          .array()
          .of(yup.string())
          .min(1, "At least one journey must be selected"),
        otherwise: undefined,
      }),
  })
  .optional()
  .default(undefined);

export const reEntryDelaySchema = yup
  .object()
  .shape({
    quantity: yup
      .number()
      .integer()
      .min(0, "Quantity must be 0 or greater")
      .required("Quantity is required when re-entry delay is set"),
    unit: yup
      .string()
      .oneOf(Object.values(IntervalUnit), "Invalid time unit")
      .required("Unit is required when re-entry delay is set"),
  })
  .optional()
  .default(undefined);

// A re-entry delay only applies when profiles can enter more than once. The UI
// hides the delay control for "Only one entry" (max_num_entries === 1), so a
// stale/partial delay inherited from a template would otherwise be validated
// while being unreachable — making the entry tile look valid yet impossible to
// save. Strip the field in that state (so a hidden value can't gate validation,
// and it's removed from the cast output), and require a full quantity+unit
// whenever the control is actually rendered and editable. Keeping this rule in
// the shared schema means the per-tile form, whole-journey, and backend
// validators all stay consistent with what the UI exposes.
const reEntryDelayFieldSchema = yup.mixed().when("max_num_entries", {
  is: (maxNumEntries: number) => maxNumEntries === 1,
  then: yup.mixed().strip(),
  otherwise: reEntryDelaySchema,
});

export const maxNumberEntrySchema = yup
  .number()
  .nullable()
  .min(
    -1,
    // -1 and 0 are allowed as other options
    // Error message prompts the user to select something greater than 0
    "Maximum number of entries must be greater than or equal to 1",
  )
  .required("Maximum number of entries is required");

export const entryCohortConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.EntryCohort]),
  max_num_entries: maxNumberEntrySchema,
  exclusions: exclusionsSchema,
  re_entry_delay: reEntryDelayFieldSchema,
});

// One entry per split group on the source audience, captured when selected
export type EntryAudienceExperimentSplitGroup = {
  id: string; // audience_splits.id
  name: string; // audience_splits.friendly_name (display-only)
  value: string; // audience_splits.column_value, aka audience_membership.ht_split_group
  isHoldout: boolean; // audience_splits.is_holdout_group
};

export type EntryAudienceExperimentConfig = EntryConfig & {
  type: JourneyNodeType.EntryAudienceExperiment;
  splitGroups: EntryAudienceExperimentSplitGroup[];
};

export type EntryAudienceExperimentBranchConfig = {
  type: JourneyNodeType.EntryAudienceExperimentBranch;
  // audience_splits.id — dormant in v1 (the snapshot only stores the value
  // string, so routing must key on groupValue); kept so the future flip to
  // id-based routing is an executor change only.
  groupId: string;
  // The ht_split_group value this branch routes (v1 routing key).
  groupValue: string;
  isHoldout: boolean;
};

// Entry routing matches ht_split_group values with exact string equality, so
// a value stored with surrounding whitespace could never match a snapshot row.
// Values are trimmed at config-capture time; this rejects anything that
// slipped past that (e.g. API-created configs) instead of storing it.
const hasNoSurroundingWhitespace = (value: string | undefined | null) =>
  value == null || value === value.trim();

const entryAudienceExperimentSplitGroupSchema = yup.object().shape({
  id: yup.string().required("Split group id is required"),
  name: yup.string().required("Split group name is required"),
  value: yup
    .string()
    .required("Split group value is required")
    .test(
      "no-surrounding-whitespace",
      "Split group value must not have leading or trailing whitespace",
      hasNoSurroundingWhitespace,
    ),
  isHoldout: yup.boolean().required(),
});

export const entryAudienceExperimentConfigSchema = yup.object().shape({
  type: yup
    .string()
    .required()
    .oneOf([JourneyNodeType.EntryAudienceExperiment]),
  max_num_entries: maxNumberEntrySchema,
  exclusions: exclusionsSchema,
  re_entry_delay: reEntryDelayFieldSchema,
  splitGroups: yup
    .array()
    .of(entryAudienceExperimentSplitGroupSchema)
    .min(1, "At least one split group is required")
    .required("Split groups are required")
    .test(
      "unique-split-group-values",
      "Split group values must be unique",
      (groups) => {
        if (!isArray(groups)) return true;
        const values = groups.map((group) => group?.value);
        return values.length === new Set(values).size;
      },
    ),
});

export const entryAudienceExperimentBranchConfigSchema = yup.object().shape({
  type: yup
    .string()
    .required()
    .oneOf([JourneyNodeType.EntryAudienceExperimentBranch]),
  groupId: yup.string().required("Split group id is required"),
  groupValue: yup
    .string()
    .required("Split group value is required")
    .test(
      "no-surrounding-whitespace",
      "Split group value must not have leading or trailing whitespace",
      hasNoSurroundingWhitespace,
    ),
  isHoldout: yup.boolean().required(),
});

export type RealtimeEntryMethod = "audience" | "event" | "criteria";

// groupId = audience_subset_groups.id, valueId = audience_subset_values.id.
export type EntryEventSubsetSelection = {
  groupId: string;
  valueId: string;
};

export type EntryEventConfig = EntryConfig & {
  type: JourneyNodeType.EntryEvent;
  max_simultaneous_entries: number;
  entry_method?: RealtimeEntryMethod;
  realtime_conditions?: RealtimeConditions;
  // last_lookup_timestamp is used exclusively by the backend to store the watermark.
  last_lookup_timestamp?: string | null; // Nullable ISO 8601 string - Expected format: YYYY-MM-DDTHH:mm:ss.sssZ
  // visual_query_conditions apply to the parent model, not the event model
  // so they are translated separately from the event_conditions.
  visual_query_conditions?: AndOrCondition<PropertyCondition>;
  // Event entry has no backing audience, so the subset selection lives here
  // rather than in audiences_subsets.
  subsets?: EntryEventSubsetSelection[];
  lookback_window?:
    | {
        relative: {
          unit: IntervalUnit;
          quantity: number;
        };
        absolute?: never;
      }
    | {
        absolute: string; // ISO 8601 string - Expected format: YYYY-MM-DDTHH:mm:ss.sssZ
        relative?: never;
      };
} & (
    | {
        event_conditions?: AndOrCondition<PropertyCondition>;
        profile_conditions?: RealtimeProfileConditions;
        segment_conditions?: never;
      }
    | {
        event_conditions?: never;
        profile_conditions?: never;
        segment_conditions?:
          | AndCondition<RootCondition>
          | OrCondition<RootCondition>;
      }
  );

const relativeDurationSchema = yup.object().shape({
  unit: yup
    .string()
    .required("Unit is required")
    .oneOf(Object.values(IntervalUnit)),
  quantity: yup
    .number()
    .min(0, "Quantity must be greater than 0")
    .required("Quantity is required")
    .integer(),
});

// Months count as 30 days and years as 365, matching the realtime worker's limit check.
const MINUTES_PER_UNIT: Record<IntervalUnit, number> = {
  [IntervalUnit.Minute]: 1,
  [IntervalUnit.Hour]: 60,
  [IntervalUnit.Day]: 60 * 24,
  [IntervalUnit.Week]: 60 * 24 * 7,
  [IntervalUnit.Month]: 60 * 24 * 30,
  [IntervalUnit.Year]: 60 * 24 * 365,
};

const MAX_REALTIME_NODE_DURATION_MINUTES = 180 * 24 * 60;

export const REALTIME_NODE_DURATION_RANGE_MESSAGE =
  "Duration must be between 1 minute and 180 days";

/** Returns whether a realtime Time delay or Hold duration is a whole quantity from one minute through 180 days. */
export const isRealtimeNodeDurationInRange = (duration: {
  unit?: unknown;
  quantity?: unknown;
}): boolean => {
  const { unit, quantity } = duration;
  if (!isIntervalUnit(unit) || !isWholeNumber(quantity)) {
    return false;
  }
  const minutes = quantity * MINUTES_PER_UNIT[unit];
  return minutes >= 1 && minutes <= MAX_REALTIME_NODE_DURATION_MINUTES;
};

const isIntervalUnit = (unit: unknown): unit is IntervalUnit =>
  typeof unit === "string" && Object.hasOwn(MINUTES_PER_UNIT, unit);

const isWholeNumber = (quantity: unknown): quantity is number =>
  typeof quantity === "number" && Number.isSafeInteger(quantity);

export const maxSimultaneousEntriesSchema = yup
  .number()
  .nullable()
  .required("Maximum number of simultaneous entries is required")
  .min(
    -1,
    // -1 and 0 are allowed as other options
    "Maximum number of simultaneous cannot be less than 1",
  )
  .integer();

export const entryEventConfigSchema = yup
  .object()
  .shape({
    type: yup.string().required().oneOf([JourneyNodeType.EntryEvent]),
    max_num_entries: maxNumberEntrySchema,
    max_simultaneous_entries: maxSimultaneousEntriesSchema,
    last_lookup_timestamp: yup
      .string()
      .matches(
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/,
        "last_lookup_timestamp must be a valid ISO 8601 string (YYYY-MM-DDTHH:mm:ss.sssZ)",
      )
      .nullable(true),
    exclusions: exclusionsSchema,
    re_entry_delay: reEntryDelayFieldSchema,
    lookback_window: yup
      .object({
        relative: relativeDurationSchema.default(null).nullable(),
        absolute: yup
          .string()
          .matches(
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/,
            "absolute must be a valid ISO 8601 string (YYYY-MM-DDTHH:mm:ss.sssZ)",
          )
          .default(null)
          .nullable(),
      })
      .test(
        "only-one",
        "Either 'relative' or 'absolute' must be set, but not both",
        (value) => {
          if (!value) return true;
          const { relative, absolute } = value;
          return !(relative && absolute);
        },
      )
      .default(null)
      .nullable(),
    subsets: yup
      .array()
      .of(
        yup.object().shape({
          groupId: yup.string().required(),
          valueId: yup.string().required(),
        }),
      )
      .optional()
      .default(undefined),
  })
  .test(
    "simultaneous-entries-constraint",
    "If max_simultaneous_entries is -1, then max_num_entries must also be -1",
    (values) => {
      // Skip this test if either value is null - those should be caught by field-level validation
      if (
        values.max_simultaneous_entries == null ||
        values.max_num_entries == null
      ) {
        return true;
      }
      if (
        values.max_simultaneous_entries === -1 &&
        values.max_num_entries !== -1
      ) {
        return false;
      }
      return true;
    },
  );

// A recurring wall-clock schedule used by the "next matching time" delay variant.
// The time-of-day is interpreted in `timezone` (IANA), and occurrences are computed
// in JS (croner/cron-parser) so day-of-week + DST handling stays out of warehouse SQL.
export type NextMatchingTimeSchedule = {
  // Grace period from node entry before matching begins. Absent/null => start immediately.
  gracePeriod?: { unit: IntervalUnit; quantity: number } | null;
  // Wall-clock time of day in 24h "HH:mm" format.
  time: string;
  // 0=Sunday..6=Saturday. Empty/omitted => every day is eligible.
  daysOfWeek?: number[];
  // IANA timezone name the wall-clock time is interpreted in.
  timezone: string;
};

export type TimeDelayConfig = {
  type: JourneyNodeType.TimeDelay;
  buffer_override_seconds?: number;
} & (
  | {
      delay: {
        unit: IntervalUnit;
        quantity: number;
      };
      propertyDelay?: never;
      absolute?: never;
      nextMatchingTime?: never;
    }
  | {
      propertyDelay: {
        offset?: {
          unit: IntervalUnit;
          quantity: number;
          direction: RelativeDirection;
        };
        referenceProperty: Pick<PropertyCondition, "property" | "propertyType">;
      };
      delay?: never;
      absolute?: never;
      nextMatchingTime?: never;
    }
  // timestamp to wait until in the format YYYY-MM-DD HH:mm:ss matches the visual query format
  | {
      absolute: string;
      delay?: never;
      propertyDelay?: never;
      nextMatchingTime?: never;
    }
  | {
      nextMatchingTime: NextMatchingTimeSchedule;
      delay?: never;
      propertyDelay?: never;
      absolute?: never;
    }
);

export const relativeDelayConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.TimeDelay]),
  delay: relativeDurationSchema.required(),
});

export const absoluteDelayConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.TimeDelay]),
  absolute: yup
    .string()
    .matches(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/,
      "absolute must be a valid ISO 8601 string (YYYY-MM-DDTHH:mm:ss.sssZ)",
    ),
});

// "HH:mm" 24-hour wall-clock time.
const TIME_OF_DAY_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export const nextMatchingTimeConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.TimeDelay]),
  nextMatchingTime: yup.object().shape({
    gracePeriod: relativeDurationSchema.default(null).nullable(),
    time: yup
      .string()
      .required("Time is required")
      .matches(TIME_OF_DAY_REGEX, "Time must be in HH:mm 24-hour format"),
    daysOfWeek: yup
      .array()
      .of(yup.number().integer().min(0).max(6))
      .optional()
      .default(undefined),
    timezone: yup
      .string()
      .required("Timezone is required")
      .test("valid-iana-timezone", "Invalid timezone", (value) => {
        if (!value) return false;
        try {
          Intl.DateTimeFormat(undefined, { timeZone: value });
          return true;
        } catch {
          return false;
        }
      }),
  }),
});

export const referencePropertyConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.TimeDelay]),
  propertyDelay: yup.object().shape({
    offset: yup.object().shape({
      direction: yup.string().oneOf(Object.values(RelativeDirection)),
      unit: yup.string().when("direction", {
        is: (direction) => direction !== undefined,
        then: (schema) =>
          schema
            .required("Unit is required")
            .oneOf(Object.values(IntervalUnit)),
        otherwise: (schema) => schema.oneOf(Object.values(IntervalUnit)),
      }),
      quantity: yup.number().when("direction", {
        is: (direction) => direction !== undefined,
        then: (schema) =>
          schema
            .required("Quantity is required")
            .min(0, "Quantity must be greater than or equal to 0")
            .integer(),
        otherwise: (schema) =>
          schema
            .min(0, "Quantity must be greater than or equal to 0")
            .integer(),
      }),
    }),
    referenceProperty: yup.object().shape({
      property: yup.mixed().required(),
      propertyType: yup
        .string()
        .required()
        .oneOf([ColumnType.Timestamp, ColumnType.Date]),
    }),
  }),
});

export type WaitUntilMethod = "event" | "segment";

/** The compiled condition tree the realtime worker reads. */
export type RealtimeConditions = RealtimeCondition;

/** How a realtime segment branch or exit criteria is authored. */
export type RealtimeConditionMethod = "event" | "criteria";

/** Checked against the profile on the pass an event trigger's event arrives, so the event alone is not enough. */
export type RealtimeProfileConditions =
  | AndCondition<RootCondition>
  | OrCondition<RootCondition>;

export type WaitUntilEventConfig = {
  type: JourneyNodeType.WaitUntilEvent;
  wait_method?: WaitUntilMethod;
  realtime_conditions?: RealtimeConditions;
  timeout_duration: {
    unit: IntervalUnit;
    quantity: number;
  };
  buffer_override_seconds?: number;
} & (
  | {
      event_conditions: AndOrCondition<PropertyCondition>;
      profile_conditions?: RealtimeProfileConditions;
      segment_conditions?: never;
    }
  | {
      event_conditions?: never;
      profile_conditions?: never;
      segment_conditions:
        | AndCondition<RootCondition>
        | OrCondition<RootCondition>;
    }
);

export const waitUntilEventConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.WaitUntilEvent]),
  timeout_duration: yup.object().shape({
    unit: yup.string().required().oneOf(Object.values(IntervalUnit)),
    quantity: yup
      .number()
      .min(0, "Quantity must be greater than or equal to 0")
      .required("Quantity is required")
      .integer(),
  }),
});

export type SegmentsConfig = {
  // All segment nodes are treated as priority lists
  type: JourneyNodeType.Segments;
  // Decides how to treat users with missing warehouse data, we either hold them or evaluate the missing condition leaf to false
  on_missing_warehouse_data?: MissingWarehouseDataBehavior;
};

export const segmentsConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.Segments]),
  on_missing_warehouse_data: yup
    .string()
    .oneOf([...MISSING_WAREHOUSE_DATA_BEHAVIORS]),
});

export type SegmentBranchConfig = {
  type: JourneyNodeType.SegmentBranch;
  branch_method?: RealtimeConditionMethod;
  realtime_conditions?: RealtimeConditions;
  // Priority order (0 is highest priority)
  segment_priority_rank: number;
  // If true, represents the "everybody else" branch of a `segments` node
  segment_is_catch_all: boolean;
  segment_conditions: AndOrCondition<RootCondition>;
};

export const segmentBranchConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.SegmentBranch]),
  segment_priority_rank: yup.number().required().integer(),
  segment_is_catch_all: yup.boolean().required(),
  branch_method: yup.string().oneOf(["event", "criteria"]).optional(),
  segment_conditions: audienceAndOrConditionSchema.when("$isRealtimeJourney", {
    is: true,
    then: audienceAndOrConditionSchema.default(undefined).notRequired(),
    otherwise: audienceAndOrConditionSchema.required(),
  }),
});

export type SyncConfig = {
  type: JourneyNodeType.Sync;
};

export const syncConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.Sync]),
});

// The Function to call and its request mapping live on the node's managed subscription, not in the node config.
export type FunctionConfig = {
  type: JourneyNodeType.Function;
};

export const functionConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.Function]),
});

export type EventEmissionMapping = Record<
  string,
  string | number | boolean | null | object
>;

export type EventEmissionConfig = {
  type: JourneyNodeType.EventEmission;
  event_model_id: number | null;
  mappings: EventEmissionMapping[];
  wait_for_event: boolean;
  send_to_event_syncs: boolean;
};

export const eventEmissionConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.EventEmission]),
  event_model_id: yup.number().integer().required(),
  mappings: yup.array().of(yup.object()).defined(),
  wait_for_event: yup.boolean().required(),
  send_to_event_syncs: yup.boolean().required(),
});

// event represents the non-timeout branch and splits into either an event query or an audience segment query
export type WaitUntilEventBranchConfig = {
  type: JourneyNodeType.WaitUntilEventBranch;
  branch: "event" | "timeout";
};

export const waitUntilEventBranchConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.WaitUntilEventBranch]),
  branch: yup.string().required().oneOf(["event", "timeout"]),
});

export type SplitsConfig = {
  type: JourneyNodeType.Splits;
  // Override to enable re-randomization
  salt_override?: string;
};

export const splitsConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.Splits]),
  salt_override: yup.string().nullable(),
});

export type SplitBranchConfig = {
  type: JourneyNodeType.SplitBranch;
  percentage: number;
};

export const splitBranchConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.SplitBranch]),
  percentage: yup.number().lessThan(100).moreThan(0).integer().required(),
});

/** Save input persisted in the feature matrix, never in decision node config. */
export type JourneyContextColumn = {
  name: string;
  // Null keeps the column's original type.
  castTo: FMColumnType | null;
};

/** GraphQL leaves an unset castTo undefined, and the stored selection reads it back as null. */
export const normalizeContextColumns = <T extends string>(
  columns: { name: string; castTo?: T | null }[],
): { name: string; castTo: T | null }[] =>
  columns.map(({ name, castTo }) => ({ name, castTo: castTo ?? null }));

export type DecisionConfig = {
  type: JourneyNodeType.Decision;
  attribution: { window: { unit: IntervalUnit; value: number } };
};

/**
 * What a decision node optimizes. Stored on decision_engine_outcomes, never on
 * the node, so a save carries it beside the node config.
 */
export type DecisionOutcome = {
  segment_id: number;
  // The same filter AID's write-attribution reads, which keys its children off
  // subconditions rather than conditions.
  filter: EventFilter | null;
};

export const decisionContextColumnsSchema = yup
  .array()
  .of(
    yup
      .object({
        name: yup.string().strict().required(),
        castTo: yup
          .string()
          .nullable()
          .oneOf([...FM_COLUMN_TYPES, null]),
      })
      .required(),
  )
  .test(
    "unique-columns",
    "Context columns must be unique",
    (columns) =>
      !columns ||
      new Set(columns.map((column) => column?.name)).size === columns.length,
  );

export const decisionConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.Decision]),
  attribution: yup
    .object()
    .shape({
      window: yup
        .object()
        .shape({
          unit: yup.string().required().oneOf(Object.values(IntervalUnit)),
          value: yup.number().required().moreThan(0).integer(),
        })
        .required(),
    })
    .required(),
});

export type MessageNodeConfig = {
  type: JourneyNodeType.Message;
  message_type: ChannelType;
  // In-app tiles only: how long an inbox item lives after the row reaches the tile. Optional for drafts, required to start the journey.
  inapp_expiration?: InAppMessageExpiration;
};

// The node library, save validation, and the send builder all read this, so a tile saved another way still cannot send WhatsApp or push.
// TODO: support WhatsApp and push Message tiles in realtime journeys.
export const REALTIME_MESSAGE_CHANNELS: ReadonlySet<string> = new Set([
  ChannelType.Email,
  ChannelType.Sms,
]);

export const messageNodeConfigSchema = yup.object().shape({
  type: yup.string().required().oneOf([JourneyNodeType.Message]),
  message_type: yup.string().required().oneOf(Object.values(ChannelType)),
  // Realtime only; see RealtimeMessageNodeConfig. Sources are checked by realtime journey validation, which knows the journey's mode.
  mappings: yup
    .array()
    .of(yup.object().shape({ to: yup.string().required() }))
    .optional(),
  inapp_expiration: yup
    .object()
    .shape({
      // Strict: save keeps the raw config, and the TTL reader rejects a numeric string.
      quantity: yup.number().strict().required().moreThan(0).integer(),
      unit: yup
        .string()
        .strict()
        .required()
        .oneOf([...INAPP_EXPIRATION_UNITS]),
    })
    .default(undefined)
    .optional(),
});

export type JourneyContextVariableType =
  | "STRING"
  | "NUMBER"
  | "BOOLEAN"
  // TIMESTAMP and JSON are only used by coupon assignment additional columns,
  // not by the static Context node (which supports STRING/NUMBER/BOOLEAN only).
  | "TIMESTAMP"
  | "JSON";

/**
 * Maps warehouse column types to their default context variable type.
 * Used by both frontend (column picker defaults) and backend (type validation).
 * Only supported column types are included — unsupported types (Null, Unknown, etc.) are excluded.
 */
export const COLUMN_TYPE_TO_CONTEXT_TYPE: Partial<
  Record<ColumnType, JourneyContextVariableType>
> = {
  [ColumnType.String]: "STRING",
  [ColumnType.Number]: "NUMBER",
  [ColumnType.BigInt]: "NUMBER",
  [ColumnType.Boolean]: "BOOLEAN",
  [ColumnType.Timestamp]: "TIMESTAMP",
  [ColumnType.Date]: "TIMESTAMP",
  [ColumnType.Json]: "JSON",
  [ColumnType.JsonArrayStrings]: "JSON",
  [ColumnType.JsonArrayNumbers]: "JSON",
};

/**
 * Maps warehouse column types to all valid context variable types.
 * Each column type can be stored as its natural type OR as STRING (cast).
 * Derived from COLUMN_TYPE_TO_CONTEXT_TYPE.
 */
export const COLUMN_TYPE_TO_VALID_CONTEXT_TYPES: Partial<
  Record<ColumnType, JourneyContextVariableType[]>
> = Object.fromEntries(
  Object.entries(COLUMN_TYPE_TO_CONTEXT_TYPE).map(([key, defaultType]) => [
    key,
    defaultType === "STRING"
      ? ["STRING"]
      : [defaultType as JourneyContextVariableType, "STRING"],
  ]),
);

export type JourneyContextVariableDefinition = {
  variable_name: string;
  variable_type: JourneyContextVariableType;
};

export type JourneyTopLevelContextConfig = {
  variables: JourneyContextVariableDefinition[];
};

export function isJourneyContextConfig(
  config: unknown,
): config is JourneyTopLevelContextConfig {
  return (
    typeof config === "object" &&
    config !== null &&
    "variables" in config &&
    Array.isArray((config as { variables: unknown }).variables)
  );
}

export const journeyContextVariableDefinitionSchema = yup.object().shape({
  variable_name: yup.string().required("Variable name is required"),
  variable_type: yup
    .string()
    .oneOf(["STRING", "NUMBER", "BOOLEAN", "TIMESTAMP", "JSON"])
    .required("Variable type is required"),
});

export const journeyTopLevelContextConfigSchema = yup.object().shape({
  variables: yup.array().of(journeyContextVariableDefinitionSchema),
});

type StringVariable = {
  variable_name: string;
  variable_type: "STRING";
  value: string;
};

type NumberVariable = {
  variable_name: string;
  variable_type: "NUMBER";
  value: number;
};

type BooleanVariable = {
  variable_name: string;
  variable_type: "BOOLEAN";
  value: boolean;
};

export type JourneyContextVariable =
  | StringVariable
  | NumberVariable
  | BooleanVariable;

export {
  JourneyContextSource,
  isHttpLookupContextConfig,
} from "./context-source";

export type JourneyLookupMapping = {
  // Column name from the HTTP Lookup sync's response extraction config.
  result_column: string;
  variable_name: string;
};

export const LOOKUP_MISSING_VALUE_ACTIONS = [
  "hold",
  "continue",
  "exit",
] as const;
// What happens to a member the lookup sync returned no result for: `hold` keeps
// it at the tile so the next journey run looks it up again.
export type JourneyLookupMissingValueAction =
  (typeof LOOKUP_MISSING_VALUE_ACTIONS)[number];

export type JourneyLookupConfig = {
  mappings: JourneyLookupMapping[];
  on_missing_value: JourneyLookupMissingValueAction;
  // Hold only: how long a member may wait at the tile for a result before
  // on_timeout applies.
  wait_for?: {
    unit: IntervalUnit;
    quantity: number;
  };
  on_timeout?: JourneySyncTimeoutAction;
};

export type ContextConfig = {
  type: JourneyNodeType.Context;
  values: JourneyContextVariable[];
  // Absent means static.
  source?: JourneyContextSource;
  lookup?: JourneyLookupConfig;
};

const variableDefinitionSchema = yup.lazy((variable) => {
  switch (variable.variable_type) {
    case "STRING":
      return yup.object().shape({
        variable_name: yup.string().label("Variable Name").required(),
        variable_type: yup.string().nullable().required().oneOf(["STRING"]),
      });
    case "NUMBER":
      return yup.object().shape({
        variable_name: yup.string().label("Variable Name").required(),
        variable_type: yup.string().nullable().required().oneOf(["NUMBER"]),
      });
    case "BOOLEAN":
      return yup.object().shape({
        variable_name: yup.string().label("Variable Name").required(),
        variable_type: yup.string().nullable().required().oneOf(["BOOLEAN"]),
      });
    default:
      return yup.object().shape({
        variable_name: yup.string().label("Variable Name").required(),
        variable_type: yup
          .string()
          .nullable()
          .label("Variable")
          .required()
          .oneOf(["STRING", "NUMBER", "BOOLEAN"], "Variable is required"),
      });
  }
});

export const ValidJourneyContextColumnNameCharacters = /^[a-z0-9_]+$/;

const variableNameValidator = yup
  .string()
  .label("Variable Name")
  .required()
  .test(
    "variable-name-validator",
    "Variable name must be lowercase and alphanumeric or underscores only",
    (value) => {
      if (value == null) return false;

      return ValidJourneyContextColumnNameCharacters.test(value);
    },
  );

const variableValueSchema = yup.lazy((variable) => {
  switch (variable.variable_type) {
    case "STRING":
      return yup.object().shape({
        variable_name: variableNameValidator,
        variable_type: yup.string().required().oneOf(["STRING"]),
        value: yup.string().nullable().label("Value").required(),
      });
    case "NUMBER":
      return yup.object().shape({
        variable_name: variableNameValidator,
        variable_type: yup.string().required().oneOf(["NUMBER"]),
        value: yup.number().nullable().label("Value").required(),
      });
    case "BOOLEAN":
      return yup.object().shape({
        variable_name: variableNameValidator,
        variable_type: yup.string().required().oneOf(["BOOLEAN"]),
        value: yup.boolean().nullable().label("Value").required(),
      });
    default:
      return yup.object().shape({
        variable_type: yup
          .string()
          .label("Variable")
          .required()
          .oneOf(["STRING", "NUMBER", "BOOLEAN"], "Variable is required"),
      });
  }
});

export const contextDefinitionsSchema = yup
  .array()
  .of(variableDefinitionSchema as any)
  .test(
    "unique-variable-name-variable-type-pair",
    "Each variable name + type must be unique",
    (list) => {
      // Should always be an array, but just in case
      if (!isArray(list)) return false;

      // Uniqueness check by converting to a string and using a Set
      const keys = list
        .filter(
          (variable) =>
            // Only look at non-empty rows
            variable && variable.variable_name && variable.variable_type,
        )
        .map((variable) =>
          JSON.stringify([variable.variable_name, variable.variable_type]),
        );

      return keys.length === new Set(keys).size;
    },
  );

export const contextValuesSchema = yup
  .array()
  .of(variableValueSchema as any)
  .test(
    "unique-variable-name-variable-type-pair",
    "Each variable name + type must be unique",
    (list) => {
      // Should always be an array, but just in case
      if (!isArray(list)) return false;

      // Uniqueness check by converting to a string and using a Set
      const keys = list
        .filter(
          (variable) =>
            // Only look at non-empty rows
            variable && variable.variable_name && variable.variable_type,
        )
        .map((variable) =>
          JSON.stringify([variable.variable_name, variable.variable_type]),
        );

      return keys.length === new Set(keys).size;
    },
  );

export const journeyLookupConfigSchema = yup.object({
  mappings: yup
    .array()
    .of(
      yup.object({
        result_column: yup.string().required("Field is required"),
        variable_name: variableNameValidator,
      }),
    )
    .min(1, "Map at least one field to a variable")
    .test(
      "unique-lookup-variable",
      "Each variable can be set by one field only",
      (list) => {
        const names = (list ?? []).map((m) => m?.variable_name).filter(Boolean);
        return names.length === new Set(names).size;
      },
    )
    .required(),
  on_missing_value: yup
    .string()
    .oneOf([...LOOKUP_MISSING_VALUE_ACTIONS])
    .required(),
  wait_for: yup.mixed().when("on_missing_value", {
    is: "hold",
    then: () => messageSyncWaitForSchema.label("Lookup wait_for"),
    otherwise: () => yup.mixed().strip(),
  }),
  on_timeout: yup.string().when("on_missing_value", {
    is: "hold",
    then: (schema) => schema.oneOf([...SYNC_TIMEOUT_ACTIONS]),
    otherwise: () => yup.mixed().strip(),
  }),
});

export const contextConfigSchema = yup.object({
  type: yup.string().required().oneOf([JourneyNodeType.Context]),
  source: yup.string().oneOf(Object.values(JourneyContextSource)).optional(),
  values: yup.mixed().when("source", {
    is: JourneyContextSource.HttpLookup,
    then: () => yup.array().max(0),
    otherwise: () => contextValuesSchema,
  }),
  lookup: yup.mixed().when("source", {
    is: JourneyContextSource.HttpLookup,
    then: () => journeyLookupConfigSchema.required(),
    otherwise: () => yup.mixed().strip(),
  }),
});

export type CouponAssignmentAdditionalColumn = {
  source_column: string;
  context_variable_name: string; // populated by UI, defaults to sanitizeVariableName(source_column)
  context_variable_type: JourneyContextVariableType;
};

export type CouponAssignmentConfig = {
  type: JourneyNodeType.CouponAssignment;
  // Model ID for the coupon pool (parent model type with is_schema: true, semantic_type: 'coupons')
  pool_model_id: string;
  // Primary assignment column — unique values drawn from the pool, always stored as STRING
  assignment_column: string;
  assignment_variable_name: string;
  // Additional pool columns to write to context log
  additional_columns?: CouponAssignmentAdditionalColumn[];
  // Property condition filter on the pool model (no relations/traits)
  filter?: AndCondition<PropertyCondition> | OrCondition<PropertyCondition>;
};

export function isCouponAssignmentConfig(
  config: unknown,
): config is CouponAssignmentConfig {
  return (
    typeof config === "object" &&
    config !== null &&
    "type" in config &&
    (config as { type: string }).type === JourneyNodeType.CouponAssignment
  );
}

export const couponAssignmentConfigSchema = yup.object().shape(
  {
    type: yup.string().required().oneOf([JourneyNodeType.CouponAssignment]),
    pool_model_id: yup.string().defined(),
    assignment_column: yup.string().when("pool_model_id", {
      is: (val: string) => Boolean(val),
      then: (schema) => schema.required("Assignment column is required"),
      otherwise: (schema) => schema.defined(),
    }),
    assignment_variable_name: yup.string().when("pool_model_id", {
      is: (val: string) => Boolean(val),
      then: (schema) => schema.required("Display name is required"),
      otherwise: (schema) => schema.defined(),
    }),
    additional_columns: yup
      .array()
      .of(
        yup.object().shape({
          source_column: yup.string().required("Column is required"),
          context_variable_name: yup
            .string()
            .required("Display name is required"),
          context_variable_type: yup
            .string()
            .oneOf(["STRING", "NUMBER", "BOOLEAN", "TIMESTAMP", "JSON"])
            .required("Variable type is required"),
        }),
      )
      .optional(),
    filter: yup.object().optional(),
  },
  [["pool_model_id", "assignment_column"]],
);

export type JourneyNodeConfig =
  | NoOpConfig
  | EntryCohortConfig
  | EntryEventConfig
  | EntryAudienceExperimentConfig
  | EntryAudienceExperimentBranchConfig
  | TimeDelayConfig
  | WaitUntilEventConfig
  | SegmentsConfig
  | SyncConfig
  | FunctionConfig
  | EventEmissionConfig
  | SplitsConfig
  | SegmentBranchConfig
  | WaitUntilEventBranchConfig
  | SplitBranchConfig
  | ContextConfig
  | CouponAssignmentConfig
  | MessageNodeConfig
  | DecisionConfig;

export const journeyNodeConfigSchema = yup.lazy((value: any) => {
  switch (value.type) {
    case JourneyNodeType.EntryCohort:
      return entryCohortConfigSchema;
    case JourneyNodeType.EntryEvent:
      return entryEventConfigSchema;
    case JourneyNodeType.EntryAudienceExperiment:
      return entryAudienceExperimentConfigSchema;
    case JourneyNodeType.EntryAudienceExperimentBranch:
      return entryAudienceExperimentBranchConfigSchema;
    case JourneyNodeType.TimeDelay:
      if ("absolute" in value) {
        return absoluteDelayConfigSchema;
      } else if ("propertyDelay" in value) {
        return referencePropertyConfigSchema;
      } else if ("nextMatchingTime" in value) {
        return nextMatchingTimeConfigSchema;
      }
      return relativeDelayConfigSchema;
    case JourneyNodeType.WaitUntilEvent:
      return waitUntilEventConfigSchema;
    case JourneyNodeType.Segments:
      return segmentsConfigSchema;
    case JourneyNodeType.Sync:
      return syncConfigSchema;
    case JourneyNodeType.Function:
      return functionConfigSchema;
    case JourneyNodeType.EventEmission:
      return eventEmissionConfigSchema;
    case JourneyNodeType.Splits:
      return splitsConfigSchema;
    case JourneyNodeType.SegmentBranch:
      return segmentBranchConfigSchema;
    case JourneyNodeType.WaitUntilEventBranch:
      return waitUntilEventBranchConfigSchema;
    case JourneyNodeType.SplitBranch:
      return splitBranchConfigSchema;
    case JourneyNodeType.Message:
      return messageNodeConfigSchema;
    case JourneyNodeType.NoOp:
      return yup.object();
    case JourneyNodeType.Context:
      return contextConfigSchema;
    case JourneyNodeType.CouponAssignment:
      return couponAssignmentConfigSchema;
    case JourneyNodeType.Decision:
      return decisionConfigSchema;
    default:
      throw new Error(
        `JourneyNodeType ${value.type} does not have a valid schema defined`,
      );
  }
});

export enum JourneyStatus {
  Enabled = "enabled",
  Disabled = "disabled",
  Draining = "draining",
  Staged = "staged",
}

export enum JourneyScheduleEndMode {
  Drain = "drain",
  HardStop = "hard_stop",
  Pause = "pause",
}

export const SCHEDULABLE_JOURNEY_STATUSES = [
  JourneyStatus.Enabled,
  JourneyStatus.Draining,
];

export enum JourneyRunStatus {
  Pending = "pending",
  Success = "success",
  Error = "error",
  InProgress = "in-progress",
}

export const TERMINAL_JOURNEY_RUN_STATUSES = [
  JourneyRunStatus.Success,
  JourneyRunStatus.Error,
];

// Terminal verdicts must go through settleJourneyRun, which enforces
// first-verdict-wins; the unguarded updateJourneyRun accepts only these.
export type NonTerminalJourneyRunStatus =
  | JourneyRunStatus.Pending
  | JourneyRunStatus.InProgress;

export enum JourneyNodeStatus {
  Pending = "pending",
  InProgress = "in-progress",
  Success = "success",
  Error = "error",
  LocalError = "local-error",
}

export function isValidJourneyNodeStatus(
  status: unknown,
): status is JourneyNodeStatus {
  return Object.values(JourneyNodeStatus).includes(status as JourneyNodeStatus);
}

export enum JourneyRunReason {
  Scheduled = "scheduled",
  ForceRemoveRows = "force-remove-rows",

  /**
   * This journey was manually run.
   */
  Manual = "manual",
}

// Reasons that a journey run could be scheduled that don't actually execute a
// full journey run. Used so the scheduler can figure out when to ignore a run
// for scheduling purposes.
export const INTERNAL_JOURNEY_RUN_REASONS = [JourneyRunReason.ForceRemoveRows];

// Type for the JSONB column in the journey_syncs table. The fields are
// mode-partitioned: `remove_after` / `remove_on_journey_exit` are Cohort-only
// (they physically delete rows from the sync table), `expire_after` is
// Trigger-only (a non-destructive read-side filter — see expireAfter in
// query.ts), and `wait_for` / `on_timeout` are Message-only. The sets are
// mutually exclusive per sync.
export type JourneySyncExitConfig = {
  // Remove the row after it has been in the sync for this amount of time.
  remove_after?: {
    unit: IntervalUnit;
    quantity: number;
    buffer_override_seconds?: number;
  };
  // Remove the row when it exits the journey.
  remove_on_journey_exit?: boolean;
  // Trigger-only: stop syncing rows whose entry is older than this window. Unlike
  // remove_after this does not delete rows — it filters them out of the sync-time
  // membership query so stale entries are never (re-)sent.
  expire_after?: {
    unit: IntervalUnit;
    quantity: number;
  };
  // Required for Message mode: how long a row may stay in the node waiting for a confirmed
  // send before on_timeout applies.
  wait_for?: {
    unit: IntervalUnit;
    quantity: number;
  };
  // Message-only: whether an unconfirmed row advances through the journey or
  // exits it once wait_for elapses.
  on_timeout?: JourneySyncTimeoutAction;
  send_in_run?: boolean | null;
};

// expire_after is validated in whole units of at least 4 hours; minutes are not
// offered as a unit (too fine-grained for a message-staleness window). Shared
// with the frontend validation + unit picker so the rule lives in one place.
export const EXPIRE_AFTER_ALLOWED_UNITS = [
  IntervalUnit.Hour,
  IntervalUnit.Day,
  IntervalUnit.Week,
  IntervalUnit.Month,
  IntervalUnit.Year,
];
export const EXPIRE_AFTER_MIN_HOUR_QUANTITY = 4;

// Must match the workspaces.journey_sync_expiry_default column default.
export const JOURNEY_SYNC_EXPIRY_WORKSPACE_DEFAULT = {
  unit: IntervalUnit.Day,
  quantity: 30,
} as const;

const expireAfterFields = {
  unit: yup.string().oneOf(EXPIRE_AFTER_ALLOWED_UNITS),
  // Floor of 4 hours: only the Hour unit can dip below a day, so require >= 4
  // there; every other allowed unit is already >= 4 hours at quantity 1.
  quantity: yup
    .number()
    .positive()
    .integer()
    .when("unit", {
      is: IntervalUnit.Hour,
      then: (schema) =>
        schema.min(
          EXPIRE_AFTER_MIN_HOUR_QUANTITY,
          "Expiry must be at least 4 hours",
        ),
    }),
};

// Fields are required here but not on the write path, where a partially filled
// form is merely incomplete rather than corrupt.
export const expireAfterSchema = yup
  .object()
  .shape({
    unit: expireAfterFields.unit.required(),
    quantity: expireAfterFields.quantity.required(),
  })
  .required();

export const messageSyncWaitForSchema = expireAfterSchema.label(
  "Message sync wait_for",
);

export const journeySyncExitConfigSchema = yup
  .object()
  .shape({
    remove_after: yup.object().shape({
      unit: yup.string().oneOf(Object.values(IntervalUnit)),
      quantity: yup.number().positive().integer(),
    }),
    remove_on_journey_exit: yup.boolean(),
    expire_after: yup.object().shape(expireAfterFields),
    wait_for: yup.object().shape(expireAfterFields),
    on_timeout: yup.string().oneOf([...SYNC_TIMEOUT_ACTIONS]),
    send_in_run: yup.boolean().nullable(),
  })
  .when("mode", {
    is: JourneyNodeSyncMode.Message,
    then: (schema) => schema.shape({ wait_for: messageSyncWaitForSchema }),
  });

// Type for the exit_criteria JSONB column in the journeys table.
export type RealtimeExitTrigger = {
  exit_method?: RealtimeConditionMethod;
  event_model_id?: string | number | null;
  event_relationship_id?: string | number | null;
  event_conditions?: AndOrCondition<PropertyCondition>;
  profile_conditions?: RealtimeProfileConditions;
};

export type JourneyExitCriteriaConfig = (
  | AndCondition<RootCondition>
  | OrCondition<RootCondition>
) &
  RealtimeExitTrigger;

export const journeyExitCriteriaSchema = yup.object().shape({
  type: yup.string().oneOf(["and", "or"]).required(),
  conditions: yup.array().defined().required(),
});

export type JourneySimulationConfig = {
  // Note: PK type will need to match the parent model's type.
  // We will need to cast the PK in the audience to match.
  // Note: If primary_key is not provided, then we will run the simulation
  // on the real entry.
  primary_key?: string;
  // ISO time the simulation stops at, in the past and at most 60 days after it starts. Without it, the simulation stops once every wait in the journey ends.
  // runJourney fills it in for a day-by-day simulation, which never runs past the present.
  end_execution_timestamp?: string;
  // ISO time the simulation's working tables were dropped. The weekly sweep drops them for stale simulations without it.
  working_tables_dropped_at?: string;
};

/**
 * Included destinations are the top 20 most used destinations with journeys
 * These are fields we want to clear out when duplicating a sync to reduce
 * the risk of duplicated syncs writing to the same external segment in the destination
 */
export const destinationTypeExternalSegmentPropertyMap: Record<
  string,
  string[]
> = {
  brazeCohorts: ["useExistingSegment", "customSegmentName", "cohortId"],
  stuzo: ["useExistingSegment", "customSegmentName", "segmentId"],
  sfmc: ["autoCreateDataExtension", "object"],
  facebook: ["useExistingSegment", "customSegmentName", "audienceId"],
  googlesheets: ["sheetName"],
  googleSheetsSA: ["sheetName"],
  iterable: ["useExistingSegment", "customSegmentName", "listId"],
  google: ["useExistingSegment", "customSegmentName", "userListName"],
  snapchat: ["useExistingSegment", "customSegmentName", "audienceId"],
  braze: ["subscriptionGroupId"],
  salesforce: [],
  "snowflake-destination": ["target", "object"],
  s3: ["objectKey"],
  sftp: ["path"],
  "marigold-engage-plus": ["campaignName"],
  "upland-waterfall": ["listId"],
  "hightouch-personalization-api": [],
  webhookV2: [],
  onedrive: ["path"],
  "display-video": [
    "useExistingSegment",
    "customSegmentName",
    "existingAudienceId",
  ],
  sendgrid: [],
};
